import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { FormControlLabel, Grid, SelectChangeEvent, Switch, TableContainer } from '@mui/material'
import { Box } from '@mui/system'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { TrendIndicator, TrendIndicatorType } from '../../../../components/TrendIndicator/TrendIndicator'
import utilsService from '../../../../services/UtilsService'
import { useMarketShareAceessCheck } from '../../../account/hooks/roleHooks'
import QuarterSelector from '../../../quarter/components/QuarterSelector'
import quarterService, { QuarterDataKey } from '../../../quarter/services/QuarterService'
import { Quarter } from '../../../quarter/types/Quarter'
import { MarketShare } from '../../types/MarketShare'
import { MarketShareNumberType, MarketShareNumberTypeId } from '../../types/MarketShareNumberType'
import { MarketShareScopeType } from '../../types/MarketShareScopeType'
import MarketShareTopGameCard from '../MarketShareTopGameCard/MarketShareTopGameCard'
import { TopGamesSortKey, getMarketShareQuarterTop3Games } from './MarketShareQuarterOverview.helpers'

interface Props {
  calculatedQuarters: Quarter[]
  marketShares: MarketShare[]
  marketShareScopeType: MarketShareScopeType
  marketShareNumberType: MarketShareNumberType
  selectedQuarter: Quarter
  onQuarterChangeCallback: (quarterIdentifier: string) => void
  onMarketShareLinkClickCallback: (marketShareName: string) => void
}

const MarketShareQuarterOverview: React.FC<Props> = ({
  calculatedQuarters,
  marketShares,
  marketShareScopeType,
  marketShareNumberType,
  selectedQuarter,
  onQuarterChangeCallback,
  onMarketShareLinkClickCallback,
}) => {
  const { t } = useTranslation()
  const [showScopeTypeShare, setShowScopeTypeShare] = useState(false)
  const [topGamesSort, setTopGamesSort] = useState<{ key: TopGamesSortKey; order: SortOrder }>({
    key: 'revenue',
    order: SortOrder.DESC,
  })
  const hasAccessToMarketShare = useMarketShareAceessCheck()

  const handleQuarterChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value as string
      onQuarterChangeCallback(value)
    },
    [onQuarterChangeCallback]
  )

  const customProps = useMemo(() => {
    return {
      marketShareScopeType,
      marketShareNumberType,
      selectedQuarter,
      showScopeTypeShare,
      setShowScopeTypeShare,
      handleQuarterChange,
      onMarketShareLinkClickCallback,
      topGamesSort,
    }
  }, [
    marketShareScopeType,
    marketShareNumberType,
    selectedQuarter,
    showScopeTypeShare,
    setShowScopeTypeShare,
    handleQuarterChange,
    onMarketShareLinkClickCallback,
    topGamesSort,
  ])

  type ColumnId = 'name' | 'revenue' | 'revenue_change' | 'downloads' | 'downloads_change' | 'top_games'

  const tableColumns: GRTableColumn<MarketShare, typeof customProps, ColumnId>[] = [
    {
      columns: [
        {
          id: 'name',
          labelAccessor: ({ customTableProps }) => (
            <>{customTableProps && customTableProps.marketShareScopeType ? t(customTableProps.marketShareScopeType.name) : ''}</>
          ),
          accessor: ({ row: marketShare, rowIndex, customTableProps }) => (
            <Link
              className={hasAccessToMarketShare ? 'MarketSharePage__MarketShareLink' : 'MarketSharePage__Disabled'}
              to={`/market-share/top-500-games`}
              onClick={(e) => (hasAccessToMarketShare ? customTableProps?.onMarketShareLinkClickCallback(marketShare.name) : e.preventDefault())}
            >
              <strong>
                {rowIndex + 1}. {marketShare.name}
              </strong>
            </Link>
          ),
          sortable: true,
          sortAccessor: 'name',
          cellProps: { align: 'left' },
          headerCellProps: { align: 'left' },
          defaultSortOrder: SortOrder.ASC,
        },
      ],
    },
    {
      labelAccessor: ({ customTableProps }) => (
        <>
          {customTableProps && (
            <QuarterSelector
              selectedQuarter={customTableProps.selectedQuarter}
              quarters={calculatedQuarters}
              onQuarterChangeCallback={(e) => customTableProps?.handleQuarterChange(e)}
            />
          )}
        </>
      ),
      columns: [
        {
          id: 'revenue',
          labelAccessor: () => <GRTooltip content={t('market-share:revenue_tooltip')}>{t('common:revenue_shorthand')}</GRTooltip>,
          accessor: ({ row: marketShare, customTableProps }) => (
            <>
              <strong>
                {customTableProps && (
                  <>
                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Percentage &&
                      utilsService.formatPercent(marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].revenuePercentage, {
                        maximumFractionDigits: 2,
                      })}

                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value &&
                      utilsService.formatCurrency(marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].revenueTop500Value, {
                        shorten: true,
                        mantissa: 2,
                      })}
                  </>
                )}
              </strong>
            </>
          ),
          sortable: true,
          sortAccessor: ({ row: marketShare, customTableProps }) => {
            if (customTableProps) {
              switch (customTableProps.marketShareNumberType.id) {
                case MarketShareNumberTypeId.Percentage:
                  return marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].revenuePercentage

                case MarketShareNumberTypeId.Value:
                  return marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].revenueTop500Value
              }
            }

            return 0
          },
          sortOrder: SortOrder.DESC,
          cellProps: { align: 'center' },
          headerCellProps: { sx: { width: 80 }, align: 'center' },
        },
        {
          id: 'revenue_change',
          labelAccessor: () => <GRTooltip content={t('market-share:revenue_change_tooltip')}>{t('common:change_text')}</GRTooltip>,
          accessor: ({ row: marketShare, customTableProps }) => (
            <>
              <strong>
                {customTableProps && (
                  <>
                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Percentage && (
                      <>
                        <TrendIndicator
                          value={
                            quarterService.getChangeDataForPreviousQuarterValue(
                              QuarterDataKey.RevenuePercentage,
                              customTableProps.selectedQuarter.quarterIdentifier,
                              marketShare.quarters
                            ) * 100
                          }
                          maximumFractionDigits={2}
                          type={TrendIndicatorType.Value}
                        />
                      </>
                    )}

                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value && (
                      <TrendIndicator
                        value={quarterService.getChangeDataForPreviousQuarterValue(
                          QuarterDataKey.RevenueTop500Value,
                          customTableProps.selectedQuarter.quarterIdentifier,
                          marketShare.quarters
                        )}
                        maximumFractionDigits={2}
                        type={TrendIndicatorType.Value}
                      />
                    )}
                  </>
                )}
              </strong>
            </>
          ),
          sortable: true,
          sortAccessor: ({ row: marketShare, customTableProps }) => {
            if (customTableProps) {
              switch (customTableProps.marketShareNumberType.id) {
                case MarketShareNumberTypeId.Percentage:
                  return quarterService.getChangeDataForPreviousQuarterValue(
                    QuarterDataKey.RevenuePercentage,
                    customTableProps.selectedQuarter.quarterIdentifier,
                    marketShare.quarters
                  )

                case MarketShareNumberTypeId.Value:
                  return quarterService.getChangeDataForPreviousQuarterValue(
                    QuarterDataKey.RevenueTop500Value,
                    customTableProps.selectedQuarter.quarterIdentifier,
                    marketShare.quarters
                  )
              }
            }

            return 0
          },
          cellProps: { align: 'center' },
          headerCellProps: { sx: { width: 80 }, align: 'center' },
        },
        {
          id: 'downloads',
          labelAccessor: () => <GRTooltip content={t('market-share:downloads_tooltip')}>{t('common:downloads_shorthand')}</GRTooltip>,
          accessor: ({ row: marketShare, customTableProps }) => (
            <>
              <strong>
                {customTableProps && (
                  <>
                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Percentage &&
                      utilsService.formatPercent(marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].downloadsPercentage, {
                        maximumFractionDigits: 2,
                      })}

                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value &&
                      utilsService.formatNumber(marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].downloadsTop500Value, {
                        shorten: true,
                        mantissa: 2,
                      })}
                  </>
                )}
              </strong>
            </>
          ),
          sortable: true,
          sortAccessor: ({ row: marketShare, customTableProps }) => {
            if (customTableProps) {
              switch (customTableProps.marketShareNumberType.id) {
                case MarketShareNumberTypeId.Percentage:
                  return marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].downloadsPercentage

                case MarketShareNumberTypeId.Value:
                  return marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier].downloadsTop500Value
              }
            }

            return 0
          },
          cellProps: { align: 'center' },
          headerCellProps: { sx: { width: 80 }, align: 'center' },
        },
        {
          id: 'downloads_change',
          labelAccessor: () => <GRTooltip content={t('market-share:downloads_change_tooltip')}>{t('common:change_text')}</GRTooltip>,
          accessor: ({ row: marketShare, customTableProps }) => (
            <>
              <strong>
                {customTableProps && (
                  <>
                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Percentage && (
                      <>
                        <TrendIndicator
                          value={
                            quarterService.getChangeDataForPreviousQuarterValue(
                              QuarterDataKey.DownloadsPercentage,
                              customTableProps.selectedQuarter.quarterIdentifier,
                              marketShare.quarters
                            ) * 100
                          }
                          maximumFractionDigits={2}
                          type={TrendIndicatorType.Value}
                        />
                      </>
                    )}

                    {customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value && (
                      <TrendIndicator
                        value={quarterService.getChangeDataForPreviousQuarterValue(
                          QuarterDataKey.DownloadsTop500Value,
                          customTableProps.selectedQuarter.quarterIdentifier,
                          marketShare.quarters
                        )}
                        maximumFractionDigits={2}
                        type={TrendIndicatorType.Value}
                      />
                    )}
                  </>
                )}
              </strong>
            </>
          ),
          sortable: true,
          sortAccessor: ({ row: marketShare, customTableProps }) => {
            if (customTableProps) {
              switch (customTableProps.marketShareNumberType.id) {
                case MarketShareNumberTypeId.Percentage:
                  return quarterService.getChangeDataForPreviousQuarterValue(
                    QuarterDataKey.DownloadsPercentage,
                    customTableProps.selectedQuarter.quarterIdentifier,
                    marketShare.quarters
                  )

                case MarketShareNumberTypeId.Value:
                  return quarterService.getChangeDataForPreviousQuarterValue(
                    QuarterDataKey.DownloadsTop500Value,
                    customTableProps.selectedQuarter.quarterIdentifier,
                    marketShare.quarters
                  )
              }
            }

            return 0
          },
          cellProps: { align: 'center' },
          headerCellProps: { sx: { width: 80 }, align: 'center' },
        },
      ],
    },
    {
      labelAccessor: ({ customTableProps }) => (
        <>
          {customTableProps && (
            <Box sx={{ width: '100%' }}>
              <GRTooltip content={t(`market-share:${customTableProps.marketShareScopeType.id}_share_tooltip`)}>
                <FormControlLabel
                  sx={{ minWidth: { sm: 500, md: 500, lg: 500 }, display: 'flex', justifyContent: 'center', marginLeft: 0 }}
                  control={
                    <Switch
                      checked={customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value ? false : customTableProps.showScopeTypeShare}
                      onChange={() => customTableProps.setShowScopeTypeShare(!customTableProps?.showScopeTypeShare)}
                      color="primary"
                      size="small"
                      disabled={customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value}
                    />
                  }
                  label={t<string>(`market-share:show_${customTableProps.marketShareScopeType.id}_share`)}
                />
              </GRTooltip>
            </Box>
          )}
        </>
      ),
      columns: [
        {
          id: 'top_games',
          labelAccessor: t('market-share:top_x_games_list_title'),
          accessor: ({ row: marketShare, customTableProps }) => (
            <>
              {customTableProps && (
                <Grid container spacing={1}>
                  {getMarketShareQuarterTop3Games(
                    marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier],
                    customTableProps.selectedQuarter,
                    customTableProps.topGamesSort,
                    customTableProps.marketShareNumberType
                  ).map((game) => {
                    return (
                      <Grid item key={game.id} xs={12} sm={4}>
                        <MarketShareTopGameCard
                          game={game}
                          selectedQuarter={customTableProps.selectedQuarter}
                          showScopeTypeShare={customTableProps.showScopeTypeShare}
                          marketShareScopeType={customTableProps.marketShareScopeType}
                          marketShareNumberType={customTableProps.marketShareNumberType}
                        />
                      </Grid>
                    )
                  })}
                  {getMarketShareQuarterTop3Games(
                    marketShare.quarters[customTableProps.selectedQuarter.quarterIdentifier],
                    customTableProps.selectedQuarter,
                    customTableProps.topGamesSort,
                    customTableProps.marketShareNumberType
                  ).length === 0 && (
                    <Box padding={1} marginTop={1}>
                      {t('common:no_games_available')}
                    </Box>
                  )}
                </Grid>
              )}
            </>
          ),
          sortable: false,
          cellProps: { align: 'center' },
        },
      ],
      headerCellProps: { sx: { display: 'flex' } },
    },
  ]

  const [columns, setColumns] = useState<GRTableColumn<MarketShare, typeof customProps, ColumnId>[]>(tableColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<MarketShare, typeof customProps, ColumnId>[]) => {
    setColumns(updatedColumns)

    const findActiveSort = (
      cols: GRTableColumn<MarketShare, typeof customProps, ColumnId>[]
    ): GRTableColumn<MarketShare, typeof customProps, ColumnId> | undefined => {
      for (const col of cols) {
        if (col.sortable && col.sortOrder) return col
        if (col.columns) {
          const child = findActiveSort(col.columns)
          if (child) return child
        }
      }
      return undefined
    }

    const activeCol = findActiveSort(updatedColumns)
    if (activeCol && (activeCol.id === 'revenue' || activeCol.id === 'revenue_change' || activeCol.id === 'downloads' || activeCol.id === 'downloads_change')) {
      setTopGamesSort({ key: activeCol.id, order: activeCol.sortOrder ?? SortOrder.DESC })
    }
  }, [])

  return (
    <div className="MarketShareQuarterOverview">
      <TableContainer>
        <GRTable
          rows={marketShares}
          columns={columns}
          rowIdKey="id"
          striped
          hoverable
          onColumnsUpdated={handleColumnsUpdate}
          customProps={customProps}
          maxRows={100}
        />
      </TableContainer>
    </div>
  )
}

export default MarketShareQuarterOverview
